import React, { useState } from 'react';
import { Icon } from '../ui/Icon.jsx';
import { Input } from '../ui/Input.jsx';
import { Button } from '../ui/Button.jsx';
import { Badge } from '../ui/Badge.jsx';
import {
  useRetailerContacts, useSaveRetailerContact,
  usePromoteRetailerContact, useDeactivateRetailerContact,
} from '../../hooks/useRetailerContacts.js';

const BLANK = { name: '', job_title: '', email: '', phone: '', whatsapp: '' };

// Only the fields the form edits — never the whole DB row, so a save can't
// round-trip stale server-owned columns (active/ended_at/is_primary/created_at)
// and resurrect a departed contact.
const toForm = (c) => ({
  id: c.id,
  name: c.name || '',
  job_title: c.job_title || '',
  email: c.email || '',
  phone: c.phone || '',
  whatsapp: c.whatsapp || '',
});

// PostgREST failures arrive as raw JSON bodies — show the operator something
// they can act on instead.
const errMsg = (e, ar) => {
  try {
    const body = JSON.parse(e.message);
    if (body.code === '23505') {
      return ar
        ? 'يوجد بالفعل جهة اتصال أساسية لهذا الحساب — حدِّث الصفحة وحاول مجدداً.'
        : 'This account already has a primary contact — refresh and try again.';
    }
    if (body.message) return body.message;
  } catch { /* not JSON — fall through to the raw message */ }
  return e.message || (ar ? 'حدث خطأ' : 'Something went wrong');
};

// ─── RETAILER CONTACTS PANEL ──────────────────────────────────────────────────
// Manages the people at one customer account. Someone leaves the customer
// company → "Left company" (kept for history, comms stop following them) and a
// successor is promoted to primary. Avoids overwriting a departed person's
// details, which used to lose the record of who actually placed past orders.
export default function RetailerContacts({ accountId, lang }) {
  const ar = lang === 'ar';
  const { data: contacts = [], isLoading } = useRetailerContacts(accountId);
  const save       = useSaveRetailerContact(accountId);
  const promote    = usePromoteRetailerContact(accountId);
  const deactivate = useDeactivateRetailerContact(accountId);

  const [form, setForm] = useState(null); // null = list; object = add/edit
  const [err, setErr]   = useState('');

  const hasActive  = contacts.some(c => c.active);
  const hasPrimary = contacts.some(c => c.active && c.is_primary);

  const submit = async (e) => {
    e.preventDefault(); setErr('');
    if (!form.name.trim()) return;
    try {
      // New contact on an account with no active people becomes the primary
      // automatically; edits never touch is_primary (use "Make primary").
      await save.mutateAsync(form.id ? form : { ...form, is_primary: !hasActive });
      setForm(null);
    } catch (e2) { setErr(errMsg(e2, ar)); }
  };

  const makePrimary = async (c) => {
    setErr('');
    try { await promote.mutateAsync(c.id); } catch (e2) { setErr(errMsg(e2, ar)); }
  };

  const markLeft = async (c) => {
    const msg = ar
      ? `تسجيل أن ${c.name} ترك الشركة؟ سيبقى في السجل للأرشيف ولن يكون جهة الاتصال الأساسية.`
      : `Mark ${c.name} as having left the company? They stay on record for history and stop being the primary contact.`;
    if (!window.confirm(msg)) return;
    setErr('');
    try { await deactivate.mutateAsync(c.id); } catch (e2) { setErr(errMsg(e2, ar)); }
  };

  return (
    <div style={{ background: 'var(--sedra-sage-wash)', borderRadius: 8, padding: 14, marginTop: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 13, fontWeight: 800, color: 'var(--sedra-forest)', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <Icon name="Users" size={14} /> {ar ? 'جهات الاتصال' : 'Contacts'}
        </div>
        {!form && (
          <Button size="sm" onClick={() => { setForm({ ...BLANK }); setErr(''); }}>
            + {ar ? 'إضافة جهة اتصال' : 'Add contact'}
          </Button>
        )}
      </div>

      {err && <div style={{ fontSize: 12, color: 'var(--status-danger)', background: 'var(--status-danger-bg)', padding: '6px 10px', borderRadius: 6, marginBottom: 8 }}>{err}</div>}
      {isLoading && <div style={{ fontSize: 12, color: 'var(--sedra-ink-muted)' }}>{ar ? 'جارٍ التحميل…' : 'Loading…'}</div>}

      {/* Deactivating the primary leaves the account without one until a
          successor is chosen — comms have no recipient until then. */}
      {hasActive && !hasPrimary && (
        <div style={{ fontSize: 12, color: 'var(--status-warn-dark)', background: 'var(--status-warn-bg)', padding: '6px 10px', borderRadius: 6, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
          <Icon name="TriangleAlert" size={12} />
          {ar ? 'لا توجد جهة اتصال أساسية لهذا الحساب — عيِّن واحدة بزر «تعيين أساسية».' : 'This account has no primary contact — pick one with "Make primary".'}
        </div>
      )}

      {form && (
        <form onSubmit={submit} style={{ background: 'var(--sedra-card)', borderRadius: 7, padding: 12, marginBottom: 10 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 10 }}>
            <Input label={ar ? 'الاسم' : 'Name'} required value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
            <Input label={ar ? 'المسمى الوظيفي' : 'Job title'} value={form.job_title}
              onChange={e => setForm(f => ({ ...f, job_title: e.target.value }))} />
            <Input label={ar ? 'البريد الإلكتروني' : 'Email'} type="email" dirValue="ltr" value={form.email}
              onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
            <Input label={ar ? 'الهاتف' : 'Phone'} dirValue="ltr" value={form.phone}
              onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} />
            <Input label={ar ? 'واتساب' : 'WhatsApp'} dirValue="ltr" value={form.whatsapp}
              onChange={e => setForm(f => ({ ...f, whatsapp: e.target.value }))} />
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <Button type="submit" size="sm" disabled={save.isPending}>
              {save.isPending ? '…' : (ar ? 'حفظ' : 'Save')}
            </Button>
            <Button type="button" size="sm" variant="secondary" onClick={() => setForm(null)}>
              {ar ? 'إلغاء' : 'Cancel'}
            </Button>
          </div>
        </form>
      )}

      {!isLoading && contacts.length === 0 && !form && (
        <div style={{ fontSize: 12, color: 'var(--sedra-ink-muted)' }}>{ar ? 'لا توجد جهات اتصال بعد.' : 'No contacts yet.'}</div>
      )}

      {contacts.map(c => (
        <div key={c.id} style={{ background: 'var(--sedra-card)', borderRadius: 7, padding: '9px 12px', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', opacity: c.active ? 1 : 0.55 }}>
          <div style={{ flex: '1 1 190px', minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--sedra-ink)', display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
              {c.name}
              {c.is_primary && c.active && (
                <Badge tone="success">{ar ? 'جهة الاتصال الأساسية' : 'PRIMARY'}</Badge>
              )}
              {!c.active && (
                <Badge tone="neutral">{ar ? 'ترك الشركة' : 'LEFT COMPANY'}</Badge>
              )}
            </div>
            <div style={{ fontSize: 11.5, color: 'var(--sedra-ink-muted)', marginTop: 2 }}>
              {[c.job_title, c.email, c.phone].filter(Boolean).join(' · ') || '—'}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
            {c.active && !c.is_primary && (
              <Button size="sm" variant="secondary" disabled={promote.isPending}
                onClick={() => makePrimary(c)}
                title={ar ? 'تعيين كجهة الاتصال الأساسية' : 'Make primary contact'}
                style={{ background: 'var(--status-success-bg)', color: 'var(--status-success)', border: 'none' }}>
                {ar ? 'تعيين أساسية' : 'Make primary'}
              </Button>
            )}
            <Button size="sm" variant="ghost" icon="Pencil"
              onClick={() => { setForm(toForm(c)); setErr(''); }}>
              {ar ? 'تعديل' : 'Edit'}
            </Button>
            {c.active && (
              <Button size="sm" variant="secondary" disabled={deactivate.isPending}
                onClick={() => markLeft(c)}
                style={{ background: 'var(--status-danger-bg)', color: 'var(--status-danger)', border: 'none' }}>
                {ar ? 'ترك الشركة' : 'Left company'}
              </Button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
